import React, { ReactElement, useState } from 'react';
import {
	CallLogData,
	DirectionCallFieldMappingConfig
} from '../../store/state/createJobState';
import Modal from '../Modal';
import { CallDirection } from '../../models/dto/callDirection';
import RadioControl from '../RadioControl';
import {
	getCallFieldMappingResult,
	getCallLogRowValue
} from '../../store/selectors/createJobSelectors';
import PreviewTable, { PreviewTableData } from '../PreviewTable';
import './DirectionMappingConfigurationModal.scss';

const maxRenderedMappings = 50;
const noSelectionValue = '___empty___';

enum DirectionMappingMode {
	Unset = 'Unset',
	Job = 'Job',
	Call = 'Call'
}

export default function DirectionMappingConfigurationModal(props: Props) {
	const [mode, setMode] = useState<DirectionMappingMode>(props.config.modeConfig.type === 'job' ? DirectionMappingMode.Job : props.config.modeConfig.columnIndex != null ? DirectionMappingMode.Call : DirectionMappingMode.Unset);
	const [jobDirection, setJobDirection] = useState<CallDirection | null>(props.config.modeConfig.type === 'job' ? props.config.modeConfig.direction : null);
	const [valueMappings, setValueMappings] = useState<Map<string, CallDirection>>(props.config.modeConfig.type === 'call' ? props.config.modeConfig.mappings : new Map());
	const [columnIndex, setColumnIndex] = useState<number | null>(props.config.modeConfig.type === 'call' ? props.config.modeConfig.columnIndex : null);

	return (
		<Modal onClose={props.onCancel} renderActions={renderActions}>
			<React.Fragment>
				<h3 className="h3-mb-3">Configure <span className="text-bold">{props.config.displayName}</span> parameters</h3>

				{(!props.hideModeSelector || mode === DirectionMappingMode.Unset) && 
					<React.Fragment>
						<div className="mb-3">How would you like to configure the call direction?</div>
						<div>
							<div className="mb-2">
								<RadioControl label="For all calls at once" selected={mode === DirectionMappingMode.Job} onSelected={() => setMode(DirectionMappingMode.Job)} />
							</div>
							<div className="mb-2">
								<RadioControl label="For each call individually (from the data)" selected={mode === DirectionMappingMode.Call} onSelected={() => setMode(DirectionMappingMode.Call)} />
							</div>
						</div>
					</React.Fragment>
				}
				
				{mode === DirectionMappingMode.Job &&
					<React.Fragment>
						<div className="mt-5 mb-2">Call direction for all calls</div>
						<select className="direction-mapping-configuration-modal__select" value={jobDirection ?? ''} onChange={e => setJobDirection(e.target.value === noSelectionValue ? null : e.target.value as CallDirection)}>
							{getSelectOptions()}
						</select>
					</React.Fragment>
				}
				
				{mode === DirectionMappingMode.Call && renderCallModeSection()}
			</React.Fragment>
		</Modal>
	);

	function renderActions(): ReactElement {
		return (
			<div className="text-right">
				<button className="btn btn_link mr-2" onClick={props.onCancel}>Cancel</button>
				<button className="btn" onClick={onDone} disabled={isDoneDisabled()}>Done</button>
			</div>
		)
	}
	
	function renderCallModeSection(): ReactElement {
		const config = getCurrentConfig();
		const column = config && config.modeConfig.type === 'call' ? config.modeConfig.columnIndex : null;
		
		return (
			<React.Fragment>
				{(column == null || !props.hideColumnSelector) &&
					<div className="mt-6">
						<div className="mb-2">Call log field</div>
						<div>
							<select className="direction-mapping-configuration-modal__select"
									value={column?.toString() ?? noSelectionValue}
									onChange={e => handleLogColumnIndexSelected(e.target.value)}>
	
								<option value={noSelectionValue}>No selection</option>
	
								{props.callLogData.headers.map((header, i) => (
									<option value={i.toString()} key={i} disabled={props.allocatedColumnIndexes.has(i)}>{header}</option>
								))}
							</select>
						</div>
					</div>
				}
				
				{renderValueMappingSection()}
			</React.Fragment>
		);
	}
	
	function handleLogColumnIndexSelected(column: string): void {
		if (column === noSelectionValue) {
			setColumnIndex(null);
		} else {
			const parsed = parseInt(column);
			if (!isNaN(parsed)) {
				setColumnIndex(parsed);
			}
		}
	}
	
	function renderValueMappingSection(): ReactElement | null {
		const previewData = getPreviewTableData();
		let mappings = getLogValueDirectionMappings();

		// in case the accidentally choose the wrong column
		// we want to protect against freezing the page while
		// we render 5000 select boxes.
		let mappingsNotShown = 0;
		if (mappings.length > maxRenderedMappings) {
			const origLength = mappings.length;
			mappings = mappings.slice(0, maxRenderedMappings);
			mappingsNotShown = origLength - mappings.length;
		}
		return (
			<div className="mt-4">
				{!mappings.length && <div>No direction values found in the data</div>}

				{!!mappings.length &&
				<div className="direction-mapping-configuration-modal__mapping-outer-container">
					<div className="direction-mapping-configuration-modal__mapping-inner-container">
						{mappings.map((m, i) => (
							<div key={i} className="direction-mapping-configuration-modal__mapping">
								<div className="direction-mapping-configuration-modal__mapping-value">{m.value || '[Empty]'} maps to</div>
								<div>
									<select className="direction-mapping-configuration-modal__mapping-select" value={m.direction ?? ''} onChange={e => handleValueMapped(m.value, e.target.value)}>
										{getSelectOptions()}
									</select>
								</div>
							</div>
						))}

						{mappingsNotShown > 0 &&
						<div className="direction-mapping-configuration-modal__extra-items-message">
							+{mappingsNotShown.toLocaleString()} item{mappingsNotShown === 1 ? '' : 's'} not shown
						</div>
						}
					</div>
				</div>
				}
				
				{!!previewData &&
					<div className="direction-mapping-configuration-modal__preview-table">
						<PreviewTable tableData={previewData} />
					</div>
				}
			</div>
		);
	}

	function getLogValueDirectionMappings(): LogValueDirectionMapping[] {
		const config = getCurrentConfig();
		if (!config) return [];
		
		const modeConfig = config.modeConfig;
		if (modeConfig.type !== 'call') return [];

		const columnIndex = modeConfig.columnIndex;
		if (columnIndex == null) return [];

		const handled: Set<string> = new Set();
		const result: LogValueDirectionMapping[] = [];

		props.callLogData.callRows.forEach(row => {
			const value = getCallLogRowValue(row, columnIndex);
			if (handled.has(value))
				return;

			result.push({ value, direction: modeConfig.mappings.get(value) });
			handled.add(value);
		});

		return result;
	}

	function getPreviewTableData(): PreviewTableData | null {
		const config = getCurrentConfig();
		if (!config) return null;
		
		const modeConfig = config.modeConfig;
		if (!modeConfig || modeConfig.type !== 'call') return null;
		
		const columnIndex = modeConfig.columnIndex;
		if (columnIndex == null) return null;
		
		const maxRowsToRender = 100;

		return {
			header: props.callLogData.headers[columnIndex] ?? 'Unknown',
			rows: props.callLogData.callRows.slice(0, maxRowsToRender).map(row => {
				return {
					rawValue: row[columnIndex] ?? '',
					mappingResult:  getCallFieldMappingResult(row, config)
				};
			}),
			rowsAreTruncated: props.callLogData.callRows.length > maxRowsToRender
		}
	}

	function handleValueMapped(value: string, direction: string): void {
		if (direction === noSelectionValue) {
			setValueMappings(prev => {
				const result = new Map(prev);
				result.delete(value);
				return result;
			});
		} else {
			setValueMappings(prev => {
				const result = new Map(prev);
				result.set(value, direction as CallDirection);
				return result;
			});
		}
	}
	
	function getCurrentConfig(): DirectionCallFieldMappingConfig | null {
		if (mode === DirectionMappingMode.Job) {
			if (jobDirection) {
				return {
					...props.config,
					modeConfig: { type: 'job', direction: jobDirection }
				};
			}
		} else if (mode === DirectionMappingMode.Call) {
			if (columnIndex != null) {
				return {
					...props.config,
					modeConfig: { type: 'call', mappings: valueMappings, columnIndex: columnIndex }
				};
			}
		}

		return null;
	}

	function isDoneDisabled(): boolean {
		return !getCurrentConfig();
	}

	function onDone(): void {
		if (isDoneDisabled()) return;

		const config = getCurrentConfig();
		if (config) {
			props.onConfigured(config);
		}
	}
}

function getSelectOptions(): ReactElement {
	return (
		<React.Fragment>
			<option value={noSelectionValue}>No selection</option>
			<option value={CallDirection.Inbound}>Inbound</option>
			<option value={CallDirection.Outbound}>Outbound</option>
			<option value={CallDirection.Internal}>Internal</option>
			<option value={CallDirection.Unknown}>Unknown</option>
		</React.Fragment>
	)
}

interface LogValueDirectionMapping {
	value: string;
	direction: CallDirection | undefined;
}

interface Props {
	config: DirectionCallFieldMappingConfig;
	callLogData: CallLogData;
	allocatedColumnIndexes: Set<number>;
	hideColumnSelector?: boolean;
	hideModeSelector?: boolean;
	onCancel: () => void;
	onConfigured: (config: DirectionCallFieldMappingConfig) => void;
}
